import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Menu,
  Stack,
  CircularProgress,
  Divider,
  ListItemIcon,
  ListItemText,
  SelectChangeEvent,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  GetApp as DownloadIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  CalendarToday as CalendarIcon,
  LocalMall as PackageIcon,
} from '@mui/icons-material';
import { useAdminOrders, OrderFilters } from '../../hooks/admin/useAdminOrders';
import {
  INK,
  FONTS,
  ghostBtnSx,
  dangerBtnSx,
  ledgerInputSx,
  ledgerSelectSx,
  editorialDialogTitleSx,
} from '../../styles/ledger';
import {
  LedgerPage,
  PageHeader,
  KPIStrip,
  KPI,
  FilterBar,
  LedgerCard,
  ActionIcon,
  StatusDot,
  MonoTag,
  EmptyState,
  ledgerTableHeadSx,
  ledgerRowSx,
  ledgerPaginationSx,
  ledgerMenuPaperSx,
  HeaderIconBtn,
} from '../../components/admin/LedgerPrimitives';

const OrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const userIdParam = queryParams.get('user_id');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [orderBy, setOrderBy] = useState<string>('created_at');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dateRange, setDateRange] = useState('This month');
  const [dateRangeAnchorEl, setDateRangeAnchorEl] = useState<null | HTMLElement>(null);

  const [filters, setFilters] = useState<OrderFilters>({
    page: page + 1,
    per_page: rowsPerPage,
    user_id: userIdParam ? parseInt(userIdParam) : undefined,
  });

  useEffect(() => {
    const updatedFilters: OrderFilters = {
      page: page + 1,
      per_page: rowsPerPage,
      search: searchTerm || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      user_id: userIdParam ? parseInt(userIdParam) : undefined,
      order_by: orderBy,
      order,
    };
    const today = new Date();
    if (dateRange === 'Today') {
      updatedFilters.date_from = today.toISOString().split('T')[0];
      updatedFilters.date_to = today.toISOString().split('T')[0];
    } else if (dateRange === 'Yesterday') {
      const y = new Date();
      y.setDate(y.getDate() - 1);
      updatedFilters.date_from = y.toISOString().split('T')[0];
      updatedFilters.date_to = y.toISOString().split('T')[0];
    } else if (dateRange === 'This week') {
      const s = new Date(today);
      s.setDate(today.getDate() - today.getDay());
      updatedFilters.date_from = s.toISOString().split('T')[0];
      updatedFilters.date_to = today.toISOString().split('T')[0];
    } else if (dateRange === 'This month') {
      const s = new Date(today.getFullYear(), today.getMonth(), 1);
      updatedFilters.date_from = s.toISOString().split('T')[0];
      updatedFilters.date_to = today.toISOString().split('T')[0];
    } else if (dateRange === 'Last 3 months') {
      const s = new Date(today);
      s.setMonth(today.getMonth() - 3);
      updatedFilters.date_from = s.toISOString().split('T')[0];
      updatedFilters.date_to = today.toISOString().split('T')[0];
    }
    setFilters(updatedFilters);
  }, [page, rowsPerPage, searchTerm, statusFilter, orderBy, order, userIdParam, dateRange]);

  const { orders, totalOrders, stats, isLoading, deleteOrder, isDeleting, refetch } = useAdminOrders(filters);

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  const handleViewOrder = (id: number) => navigate(`/admin/orders/${id}`);
  const handleEditOrder = (id: number) => navigate(`/admin/orders/${id}/edit`);

  const formatCurrency = (amount: number, currency: string = 'ILS') =>
    new Intl.NumberFormat('en-IL', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);

  const statusTone = (status: string, shipmentStatus?: string): 'active' | 'inactive' | 'warning' | 'info' | 'neutral' => {
    if (shipmentStatus === 'delivered' || status === 'delivered') return 'active';
    if (status === 'cancelled' || shipmentStatus === 'cancelled') return 'inactive';
    if (status === 'pending' || status === 'draft' || shipmentStatus === 'pending') return 'warning';
    if (
      shipmentStatus === 'in_transit' ||
      shipmentStatus === 'out_for_delivery' ||
      status === 'shipped' ||
      status === 'processing' ||
      shipmentStatus === 'scheduled_for_pickup' ||
      shipmentStatus === 'ready'
    )
      return 'info';
    return 'neutral';
  };

  const renderSortHeader = (id: string, label: string) => {
    const isActive = orderBy === id;
    return (
      <TableCell
        sortDirection={isActive ? order : false}
        sx={{
          cursor: 'pointer',
          userSelect: 'none',
          color: isActive ? `${INK.accent} !important` : 'inherit',
          '&:hover': { color: `${INK.accent} !important` },
        }}
        onClick={() => handleRequestSort(id)}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {label}
          {isActive && (order === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 12 }} /> : <ArrowDownwardIcon sx={{ fontSize: 12 }} />)}
        </Stack>
      </TableCell>
    );
  };

  return (
    <LedgerPage eyebrowFooter={`Almaship · Orders · ${String(totalOrders).padStart(4, '0')} records`}>
      <PageHeader
        eyebrow="Admin · Manifest"
        title={
          <>
            Orders
            <Box component="span" sx={{ fontStyle: 'italic', color: INK.accent }}>
              .
            </Box>
          </>
        }
        description={`Shipping orders, payments, and fulfillment status${userIdParam ? ` — filtered for user #${userIdParam}` : ''}.`}
        actions={
          <>
            <Button
              onClick={(e) => setDateRangeAnchorEl(e.currentTarget)}
              startIcon={<CalendarIcon sx={{ fontSize: 16 }} />}
              sx={ghostBtnSx}
            >
              {dateRange}
            </Button>
            <Menu
              anchorEl={dateRangeAnchorEl}
              open={Boolean(dateRangeAnchorEl)}
              onClose={() => setDateRangeAnchorEl(null)}
              PaperProps={{ sx: { ...ledgerMenuPaperSx, minWidth: 160 } }}
            >
              {['Today', 'Yesterday', 'This week', 'This month', 'Last 3 months', 'Custom range'].map((r) => (
                <MenuItem key={r} onClick={() => { setDateRange(r); setDateRangeAnchorEl(null); }}>
                  {r}
                </MenuItem>
              ))}
            </Menu>
            <HeaderIconBtn title="Refresh" onClick={() => refetch()}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </HeaderIconBtn>
            <Button startIcon={<DownloadIcon sx={{ fontSize: 16 }} />} sx={ghostBtnSx}>
              Export
            </Button>
          </>
        }
      />

      <KPIStrip cols={6}>
        <KPI label="Total" value={stats.total_orders} sub="all orders" />
        <KPI label="Processing" value={stats.processing_orders} sub="in progress" accent />
        <KPI label="Shipped" value={stats.shipped_orders} sub="in transit" />
        <KPI label="Delivered" value={stats.delivered_orders} sub="completed" />
        <KPI label="Cancelled" value={stats.cancelled_orders} sub="voided" />
        <KPI label="Revenue" value={formatCurrency(stats.total_revenue)} sub="this range" />
      </KPIStrip>

      <FilterBar>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by order ID, customer, email…"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: INK.subtle }} />
                </InputAdornment>
              ),
            }}
            sx={ledgerInputSx}
          />
        </Box>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel sx={{ fontFamily: FONTS.sans, fontSize: 13, color: INK.muted, '&.Mui-focused': { color: INK.accent } }}>
            Status
          </InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e: SelectChangeEvent) => { setStatusFilter(e.target.value); setPage(0); }}
            sx={ledgerSelectSx}
          >
            {['all', 'pending', 'processing', 'shipped', 'delivered', 'cancelled'].map((s) => (
              <MenuItem key={s} value={s} sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>
                {s === 'all' ? 'All status' : s.charAt(0).toUpperCase() + s.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </FilterBar>

      <LedgerCard padded={false}>
        <TableContainer>
          <Table sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow sx={ledgerTableHeadSx}>
                {renderSortHeader('id', 'Order')}
                {renderSortHeader('user_id', 'Customer')}
                {renderSortHeader('created_at', 'Date')}
                {renderSortHeader('total_price', 'Total')}
                <TableCell>Items</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right" sx={{ width: 140 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: 'center', py: 10, borderBottom: 'none' }}>
                    <CircularProgress size={28} sx={{ color: INK.accent }} />
                  </TableCell>
                </TableRow>
              ) : orders.length > 0 ? (
                orders.map((order) => {
                  const id = order.id;
                  const orderNumber = order.order_number || order.orderNumber || `Order ${id}`;
                  const trackingNumber = order.tracking_number || order.trackingNumber;
                  const totalPrice = order.total_price || order.totalPrice || 0;
                  const createdAt = order.created_at || order.createdAt || '';
                  const status = order.order_status || order.status || 'processing';
                  const shipmentStatus = order.shipment_status;
                  const service = order.service || (order.selected_rate && order.selected_rate.name) || 'Standard';
                  const paymentMethod = order.payment_method || order.paymentMethod || '';
                  const parcels = order.parcels || [];
                  const fromAddress = order.from_address || order.fromAddress;
                  const itemCount = parcels.reduce((sum, p) => sum + (p.quantity || 1), 0);
                  const tone = statusTone(status, shipmentStatus);

                  return (
                    <TableRow key={id} sx={ledgerRowSx}>
                      <TableCell>
                        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 13, color: INK.text, fontWeight: 600 }}>
                          {orderNumber}
                        </Typography>
                        {trackingNumber && (
                          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10.5, color: INK.subtle, mt: 0.25, letterSpacing: '0.04em' }}>
                            TRK · {trackingNumber}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 13, color: INK.text, fontWeight: 500, letterSpacing: '-0.005em' }}>
                          {fromAddress?.contactName || 'Unknown'}
                        </Typography>
                        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.subtle }}>
                          {fromAddress?.email || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 12, color: INK.muted }}>
                          {new Date(createdAt).toLocaleDateString('en-CA')}
                        </Typography>
                        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10, color: INK.faint, mt: 0.25, letterSpacing: '0.1em' }}>
                          {new Date(createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 13.5, color: INK.text, fontWeight: 600, letterSpacing: '-0.01em' }}>
                          {formatCurrency(totalPrice, order.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 12, color: INK.text }}>
                          ×{itemCount}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <MonoTag>{service}</MonoTag>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11.5, color: INK.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          {paymentMethod || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusDot
                          label={status.replace(/_/g, ' ')}
                          tone={tone}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack className="row-actions" direction="row" spacing={0.5} justifyContent="flex-end" sx={{ opacity: 0.45, transition: 'opacity 0.15s' }}>
                          <ActionIcon title="View" onClick={() => handleViewOrder(id)}>
                            <VisibilityIcon sx={{ fontSize: 15 }} />
                          </ActionIcon>
                          <ActionIcon title="Edit" onClick={() => handleEditOrder(id)}>
                            <EditIcon sx={{ fontSize: 15 }} />
                          </ActionIcon>
                          <ActionIcon title="More" onClick={(e) => { setMenuAnchorEl(e.currentTarget); setSelectedOrderId(id); }}>
                            <MoreVertIcon sx={{ fontSize: 15 }} />
                          </ActionIcon>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={9} sx={{ borderBottom: 'none' }}>
                    <EmptyState
                      icon={<SearchIcon sx={{ fontSize: 22 }} />}
                      title="No orders on record."
                      description="Try adjusting your search, status, or date range."
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalOrders}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={ledgerPaginationSx}
        />
      </LedgerCard>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{ sx: { ...ledgerMenuPaperSx, minWidth: 200 } }}
      >
        <MenuItem onClick={() => { if (selectedOrderId) handleViewOrder(selectedOrderId); setMenuAnchorEl(null); }}>
          <ListItemIcon><VisibilityIcon sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText primary="View details" />
        </MenuItem>
        <MenuItem onClick={() => { if (selectedOrderId) handleEditOrder(selectedOrderId); setMenuAnchorEl(null); }}>
          <ListItemIcon><EditIcon sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText primary="Edit order" />
        </MenuItem>
        <MenuItem onClick={() => setMenuAnchorEl(null)}>
          <ListItemIcon><ReceiptIcon sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText primary="Generate invoice" />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => setMenuAnchorEl(null)}>
          <ListItemIcon><PackageIcon sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText primary="Update status" />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => { if (selectedOrderId) { setDeleteDialogOpen(true); setMenuAnchorEl(null); } }}
          sx={{ color: `${INK.inactive} !important`, '& .MuiListItemIcon-root': { color: `${INK.inactive} !important` } }}
        >
          <ListItemIcon><DeleteIcon sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText primary="Delete order" />
        </MenuItem>
      </Menu>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 0.5 } }}>
        <DialogTitle sx={editorialDialogTitleSx}>Delete order</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: FONTS.sans, color: INK.muted, mt: 2 }}>
            Are you sure you want to delete this order? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={ghostBtnSx}>Cancel</Button>
          <Button
            onClick={async () => {
              if (selectedOrderId) {
                try {
                  await deleteOrder(selectedOrderId);
                  setDeleteDialogOpen(false);
                } catch (err) {
                  console.error(err);
                }
              }
            }}
            disabled={isDeleting}
            sx={dangerBtnSx}
            startIcon={isDeleting ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <DeleteIcon sx={{ fontSize: 16 }} />}
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </LedgerPage>
  );
};

export default OrdersPage;
